import { useEffect, type ReactNode } from 'react'
import { Ban, Check, CheckCircle2, Download, Trash2, UploadCloud } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Textarea } from '@/shared/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group'
import { cn } from '@/shared/lib/utils'
import { parseBetweenBounds, parseNumericRangeBounds } from '@/shared/lib/data-field-bounds'
import { t } from '@/shared/lib/i18n'
import type WebInstrumentsTaskLogDataField from '@/shared/types/web/WebInstrumentsTaskLogDataField'

export const LINEAR_ACCEPTABLE = 'acceptable'
export const LINEAR_UNACCEPTABLE = 'unacceptable'

/** Large lab-friendly option chip (REBUILD-470) — selected via RadioGroupItem sr-only. */
const OPTION_BUTTON_CLASS =
  'inline-flex min-h-10 min-w-[5.5rem] cursor-pointer items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground peer-data-[state=checked]:hover:bg-primary peer-data-[state=checked]:hover:text-primary-foreground'

type OptionButton = {
  id: string
  value: string
  label: string
  hint?: ReactNode
}

function OptionButtonGroup({
  value,
  onValueChange,
  options,
  ariaLabelledBy,
  hasError = false,
}: {
  value: string
  onValueChange: (nextValue: string) => void
  options: OptionButton[]
  ariaLabelledBy?: string
  hasError?: boolean
}) {
  return (
    <RadioGroup
      aria-labelledby={ariaLabelledBy}
      value={value}
      onValueChange={onValueChange}
      className={cn('flex flex-wrap gap-2', hasError && 'rounded-md border border-destructive p-2')}
    >
      {options.map((option) => (
        <div key={option.value} className="flex flex-col gap-1">
          <div className="relative">
            <RadioGroupItem id={option.id} value={option.value} className="peer sr-only" />
            <Label htmlFor={option.id} className={OPTION_BUTTON_CLASS}>
              {option.label}
            </Label>
          </div>
          {option.hint}
        </div>
      ))}
    </RadioGroup>
  )
}

// Legacy parity (restrictInput "onlyNumbers"): a number field accepts an optional
// leading minus, digits, and a single decimal point. Empty string is allowed so the
// user can clear the field. Used to reject keystrokes that would make the value non-numeric.
const NUMERIC_INPUT_PATTERN = /^-?\d*(\.\d*)?$/

export const isAllowedNumericInput = (value: string): boolean => NUMERIC_INPUT_PATTERN.test(value)

export interface DataFieldEntry {
  [key: string]: string | number
  instrumentTaskDataFieldId: number
  value: string
  linearAcceptable: string
}

export interface DataFieldFile {
  [key: string]: string | number
  instrumentTaskDataFieldId: number
  attachmentName: string
  mimeType: string
  dataBase64: string
}

/** Already-persisted attachment shown for a file-attachment field in edit mode. */
export interface ExistingDataFieldAttachment {
  id: number
  attachmentName: string | null
  downloadHref: string | null
}

interface DataFieldInputsProps {
  dataFields: WebInstrumentsTaskLogDataField[]
  entries: DataFieldEntry[]
  errors?: Record<string, string | string[]>
  onUpdateValue: (fieldId: number, value: string) => void
  onUpdateLinearAcceptable: (fieldId: number, choice: string) => void
  /** Newly picked (not yet persisted) file per field; only `attachmentName` is read for display. */
  dataFieldFiles: Record<number, { attachmentName: string } | null>
  onFileSelected: (fieldId: number, file: File | null) => void
  onFileRemoved: (fieldId: number) => void
  /** Persisted attachments keyed by field id (edit mode); resolve modal omits this. */
  existingAttachmentByField?: Record<number, ExistingDataFieldAttachment>
  /** Field ids whose existing attachment the user removed in this session. */
  removedAttachmentFieldIds?: number[]
  /** Render the file-size hint under attachment fields (full log page only). */
  showSizeHint?: boolean
  /**
   * Reports whether every data field entry is valid for submission. Currently reflects the
   * linear "Unacceptable but value is acceptable" mismatch (legacy sweet-alert block).
   */
  onValidityChange?: (valid: boolean) => void
  /**
   * Reports whether a comment must be required (legacy checkCommentRequired): true when any
   * field has an entered value the config rejects. The caller enforces the required comment.
   */
  onCommentRequiredChange?: (required: boolean) => void
  /**
   * When true (default) the inputs are wrapped in a bordered box with a "Measurements" heading.
   * Set false when the caller provides its own section wrapper / empty-state.
   */
  showContainer?: boolean
}

const VALIDATION_RULE_KEY: Record<string, string> = {
  range_gt: 'instruments.taskLogs.validationRuleGt',
  range_gt_eq: 'instruments.taskLogs.validationRuleGtEq',
  range_lt: 'instruments.taskLogs.validationRuleLt',
  range_lt_eq: 'instruments.taskLogs.validationRuleLtEq',
  equals: 'instruments.taskLogs.validationRuleEquals',
  range_bt: 'instruments.taskLogs.validationRuleBetween',
}

const validationRuleLabel = (validationType: string): string => {
  const key = VALIDATION_RULE_KEY[validationType]
  return key ? t(key as Parameters<typeof t>[0]) : ''
}

const formatValidationRule = (validationType: string | null, value: string): string => {
  const label = validationType ? validationRuleLabel(validationType) : ''
  if (validationType === 'range_bt') {
    const numericBounds = parseNumericRangeBounds(value)
    if (numericBounds) {
      return `${label} ${numericBounds[0]} - ${numericBounds[1]}`
    }

    const displayBounds = parseBetweenBounds(value)
    if (displayBounds.min || displayBounds.max) {
      return `${label} ${displayBounds.min} - ${displayBounds.max}`
    }
  }
  return label ? `${label} ${value}` : value
}

const acceptableRangeSummary = (
  acceptableValues: WebInstrumentsTaskLogDataField['acceptableValues'],
): string => {
  // Defined-type fields have no numeric band: legacy (calendarView.component.ts) lists the
  // acceptable picklist values (is_acceptable rows) joined by comma, so mirror that.
  const definedAcceptable = acceptableValues
    .filter((row) => row.validationType === 'defined' && row.isAcceptable && row.value)
    .map((row) => String(row.value))
  if (definedAcceptable.length > 0) return definedAcceptable.join(', ')

  // Show the configured numeric range regardless of the rule's is_acceptable verdict, mirroring the
  // server-side validation_range label (DataFieldLog serializer takes the first active rule). A rule
  // whose match means "unacceptable" still defines the band the reading is compared against.
  const rules = acceptableValues.filter(
    (row) => row.value != null && row.value !== '' && row.validationType !== 'defined',
  )
  if (rules.length === 0) return ''
  const useAnd = rules.length > 1 && rules[0].operatorType?.toUpperCase() === 'AND'
  const separator = useAnd ? ' AND ' : ' OR '
  return rules
    .map((row) => formatValidationRule(row.validationType, String(row.value)))
    .join(separator)
}

const firstError = (raw: string | string[] | undefined): string | undefined => {
  if (raw == null) return undefined
  return Array.isArray(raw) ? raw[0] : raw
}

// Mirrors the server-side InstrumentTaskDataFieldValues::AcceptableValueEvaluator so the form's
// live Acceptable/Unacceptable badge never diverges from the stored qc_passed verdict:
//   - consider ALL active numeric rules, not just is_acceptable ones. A rule's is_acceptable flag
//     is the *verdict* it assigns, not a filter — a config can declare a range whose match means
//     "unacceptable" (e.g. value "2..8" with is_acceptable=false). Filtering to is_acceptable=true
//     dropped such rules to zero and made every value read as Acceptable (REBUILD-QC-badge).
//   - find the matching rule (AND: all must match -> first rule's verdict; OR: first matching wins).
//   - a matched rule yields its is_acceptable verdict; NO match is a rejected value (out of range).
export const isValueOutOfRange = (
  value: string,
  acceptableValues: WebInstrumentsTaskLogDataField['acceptableValues'],
): boolean => {
  if (!value || acceptableValues.length === 0) return false
  const numericValue = parseFloat(value)
  if (Number.isNaN(numericValue)) return false

  // Defined-type rules are not numeric — they are handled separately via isDataFieldEntryUnacceptable.
  const rules = acceptableValues.filter(
    (row) => row.value != null && row.value !== '' && row.validationType !== 'defined',
  )
  if (rules.length === 0) return false

  const useAnd = rules.length > 1 && rules[0].operatorType?.toUpperCase() === 'AND'

  const ruleMatches = (rule: (typeof rules)[number]): boolean => {
    const type = rule.validationType ?? ''
    if (type === 'range_bt') {
      const bounds = parseNumericRangeBounds(rule.value as string)
      return bounds != null && numericValue >= bounds[0] && numericValue <= bounds[1]
    }
    const threshold = parseFloat(rule.value as string)
    if (Number.isNaN(threshold)) return false
    if (type.includes('gt_eq')) return numericValue >= threshold
    if (type.includes('gt')) return numericValue > threshold
    if (type.includes('lt_eq')) return numericValue <= threshold
    if (type.includes('lt')) return numericValue < threshold
    if (type === 'equals') return numericValue === threshold
    return false
  }

  // AND: the value must satisfy every rule, then the first rule's verdict applies.
  // OR: the first matching rule's verdict applies. No match at all is a rejected value.
  const matched = useAnd
    ? rules.every(ruleMatches)
      ? rules[0]
      : undefined
    : rules.find(ruleMatches)

  if (!matched) return true
  return !matched.isAcceptable
}

// True only when the field has acceptable rules, the value is numeric, and it satisfies
// every acceptable rule (i.e. the value would be considered "acceptable" by the config).
export const isValueInAcceptableRange = (
  value: string,
  acceptableValues: WebInstrumentsTaskLogDataField['acceptableValues'],
): boolean => {
  if (!value || acceptableValues.length === 0) return false
  if (Number.isNaN(parseFloat(value))) return false
  return !isValueOutOfRange(value, acceptableValues)
}

// Legacy bug (instrumentTasks.component.ts): the user picked "Unacceptable" but the entered
// value actually satisfies the acceptable condition — the log must be blocked until resolved.
export const isLinearUnacceptableMismatch = (
  linearAcceptable: string,
  value: string,
  dataField: WebInstrumentsTaskLogDataField,
): boolean =>
  linearAcceptable === LINEAR_UNACCEPTABLE &&
  dataField.requiresValidation &&
  isValueInAcceptableRange(value, dataField.acceptableValues)

// Legacy parity (checkCommentRequired): a single field is "unacceptable" when it has an
// entered value that the config rejects. Mirrors legacy `value != '' && !acceptableValue`
// across the validated field types (number out of range, linear declared Unacceptable,
// defined selection whose row is not acceptable).
export const isDataFieldEntryUnacceptable = (
  entry: { value: string; linearAcceptable: string } | undefined,
  dataField: WebInstrumentsTaskLogDataField,
): boolean => {
  if (!dataField.requiresValidation) return false

  const value = entry?.value ?? ''

  if (dataField.dataFieldType === 'linear') {
    return entry?.linearAcceptable === LINEAR_UNACCEPTABLE
  }

  if (dataField.dataFieldType === 'defined') {
    if (!value) return false
    const normalizedValue = value.trim().toLowerCase()
    const match = dataField.acceptableValues.find(
      (row) =>
        String(row.value ?? '')
          .trim()
          .toLowerCase() === normalizedValue,
    )
    // Explicit acceptableValues row: use its is_acceptable verdict (pass → false, fail row → true).
    if (match) return !match.isAcceptable

    // Typical Pass/Fail payload: only Pass is in acceptableValues. Fail lives only in
    // definedValues. Mirror AcceptableValueEvaluator / legacy: a selected defined option
    // that does not match an acceptable pass is unacceptable while entering (REBUILD-530).
    const isConfiguredDefinedOption = dataField.definedValues.some(
      (definedValue) => definedValue.value.trim().toLowerCase() === normalizedValue,
    )
    return isConfiguredDefinedOption
  }

  if (value.trim() === '') return false
  return isValueOutOfRange(value, dataField.acceptableValues)
}

// True when any field forces a required comment (legacy commentRequired banner).
export const anyDataFieldUnacceptable = (
  dataFields: WebInstrumentsTaskLogDataField[],
  entries: { instrumentTaskDataFieldId: number; value: string; linearAcceptable: string }[],
): boolean =>
  dataFields.some((dataField) =>
    isDataFieldEntryUnacceptable(
      entries.find((entry) => entry.instrumentTaskDataFieldId === dataField.id),
      dataField,
    ),
  )

// Consistent "Unacceptable" status badge shared across every validated field type
// (numeric out-of-range, linear declared Unacceptable, defined non-acceptable selection).
const UnacceptableBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
    <Ban className="h-3.5 w-3.5" />
    {t('instruments.taskLogs.number.unacceptable')}
  </span>
)

/**
 * Renders the per-field input controls for a set of InstrumentTaskDataFields.
 * Single source of truth shared between the full task-log page (`Instruments/Tasks/Log.tsx`)
 * and the Resolve-the-Miss modal so both paths produce identical inputs and validation behaviour.
 */
export const DataFieldInputs = ({
  dataFields,
  entries,
  errors,
  onUpdateValue,
  onUpdateLinearAcceptable,
  dataFieldFiles,
  onFileSelected,
  onFileRemoved,
  existingAttachmentByField,
  removedAttachmentFieldIds = [],
  showSizeHint = false,
  onValidityChange,
  onCommentRequiredChange,
  showContainer = true,
}: DataFieldInputsProps) => {
  const hasLinearMismatch = dataFields.some((dataField) => {
    const entry = entries.find((e) => e.instrumentTaskDataFieldId === dataField.id)
    return isLinearUnacceptableMismatch(
      entry?.linearAcceptable ?? '',
      entry?.value ?? '',
      dataField,
    )
  })

  const commentRequired = anyDataFieldUnacceptable(dataFields, entries)

  useEffect(() => {
    onValidityChange?.(!hasLinearMismatch)
  }, [hasLinearMismatch, onValidityChange])

  useEffect(() => {
    onCommentRequiredChange?.(commentRequired)
  }, [commentRequired, onCommentRequiredChange])

  if (dataFields.length === 0) return null

  const fields = (
    <div className="space-y-4">
      {dataFields.map((dataField) => {
        const entry = entries.find((e) => e.instrumentTaskDataFieldId === dataField.id)
        const value = entry?.value ?? ''
        const errMsg =
          firstError(errors?.[`dataField${dataField.id}`]) ??
          firstError(errors?.[`data_field_${dataField.id}`])

        const fieldType = dataField.dataFieldType
        const selectOptions =
          dataField.definedValues.length > 0
            ? dataField.definedValues.map((definedValue) => ({
                value: definedValue.value,
                label: definedValue.value,
              }))
            : []
        const hasDefinedOptions = fieldType === 'defined' && selectOptions.length > 0
        const rangeSummary = acceptableRangeSummary(dataField.acceptableValues)
        const pickedFile = dataFieldFiles[dataField.id] ?? null
        const existingAttachment = existingAttachmentByField?.[dataField.id]
        const attachmentRemoved = removedAttachmentFieldIds.includes(dataField.id)
        const showExistingAttachment =
          Boolean(existingAttachment) && !attachmentRemoved && !pickedFile
        const entryUnacceptable = isDataFieldEntryUnacceptable(entry, dataField)

        return (
          <div key={dataField.id} className="space-y-1.5">
            <Label id={`df-${dataField.id}-label`} htmlFor={`df-${dataField.id}`}>
              {dataField.label ?? t('instruments.taskLogs.unnamedField')}
              {!dataField.required ? ` (${t('instruments.taskLogs.notRequired')})` : ''}
            </Label>

            {dataField.dataFieldDescription && (
              <p className="text-muted-foreground text-xs break-words">
                {dataField.dataFieldDescription}
              </p>
            )}

            {fieldType !== 'linear' && rangeSummary && (
              <p className="text-xs text-amber-600 break-words">
                {t('instruments.taskLogs.acceptableRange')}: {rangeSummary}
              </p>
            )}

            {fieldType === 'textarea' ? (
              <Textarea
                id={`df-${dataField.id}`}
                value={value}
                rows={3}
                placeholder={t('instruments.taskLogs.textareaPlaceholder')}
                onChange={(e) => onUpdateValue(dataField.id, e.target.value)}
                className={cn('break-all', errMsg && 'border-destructive')}
              />
            ) : fieldType === 'linear' ? (
              <div className="space-y-2">
                <OptionButtonGroup
                  ariaLabelledBy={`df-${dataField.id}-label`}
                  value={entry?.linearAcceptable || ''}
                  onValueChange={(choice) => onUpdateLinearAcceptable(dataField.id, choice)}
                  options={[
                    {
                      id: `df-${dataField.id}-acceptable`,
                      value: LINEAR_ACCEPTABLE,
                      label: t('instruments.taskLogs.linear.acceptable'),
                      hint: rangeSummary ? (
                        <p className="max-w-xs text-xs text-muted-foreground break-words">
                          {t('instruments.taskLogs.linear.acceptableHint')}: {rangeSummary}
                        </p>
                      ) : undefined,
                    },
                    {
                      id: `df-${dataField.id}-unacceptable`,
                      value: LINEAR_UNACCEPTABLE,
                      label: t('instruments.taskLogs.linear.unacceptable'),
                    },
                  ]}
                />
                <Input
                  id={`df-${dataField.id}`}
                  inputMode="decimal"
                  value={value}
                  placeholder={t('instruments.taskLogs.linear.valuePlaceholder')}
                  disabled={entry?.linearAcceptable !== LINEAR_UNACCEPTABLE}
                  onChange={(e) => {
                    if (isAllowedNumericInput(e.target.value))
                      onUpdateValue(dataField.id, e.target.value)
                  }}
                  className={cn('max-w-xs', errMsg && 'border-destructive')}
                />
                {dataField.requiresValidation &&
                  isValueOutOfRange(value, dataField.acceptableValues) && (
                    <p className="text-xs text-amber-600">
                      {t('instruments.taskLogs.validationWarning')}
                    </p>
                  )}
                {isLinearUnacceptableMismatch(entry?.linearAcceptable ?? '', value, dataField) && (
                  <p className="text-xs text-destructive">
                    {t('instruments.taskLogs.linear.unacceptableMismatch')}
                  </p>
                )}
                {entryUnacceptable && <UnacceptableBadge />}
              </div>
            ) : fieldType === 'fileAttachment' ? (
              <div className="space-y-2">
                {pickedFile && (
                  <div className="flex items-center gap-2 overflow-hidden text-sm">
                    <span className="min-w-0 flex-1 max-w-[250px] whitespace-break-spaces truncate font-medium">
                      {pickedFile.attachmentName}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0"
                      onClick={() => onFileRemoved(dataField.id)}
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" />
                      {t('instruments.taskLogs.fileAttachment.remove')}
                    </Button>
                  </div>
                )}
                {showExistingAttachment && existingAttachment && (
                  <div className="flex items-center gap-2 overflow-hidden text-sm">
                    <a
                      href={existingAttachment.downloadHref ?? '#'}
                      className="flex min-w-0 flex-1 items-center text-primary underline-offset-2 hover:underline"
                      download
                    >
                      <Download className="mr-1 h-3.5 w-3.5 shrink-0" />
                      <span className="min-w-0 flex-1 truncate max-w-[250px]">
                        {existingAttachment.attachmentName}
                      </span>
                    </a>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0"
                      onClick={() => onFileRemoved(dataField.id)}
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" />
                      {t('instruments.taskLogs.fileAttachment.remove')}
                    </Button>
                  </div>
                )}
                {!pickedFile && (
                  <label
                    htmlFor={`df-${dataField.id}-file`}
                    className={cn(
                      'flex w-fit cursor-pointer items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm transition-colors hover:bg-muted/50 focus-within:ring-ring/50 focus-within:ring-[3px]',
                      errMsg && 'border-destructive',
                    )}
                  >
                    <UploadCloud className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-primary">
                      {showExistingAttachment
                        ? t('instruments.taskLogs.fileAttachment.replaceFile')
                        : t('instruments.taskLogs.fileAttachment.chooseFile')}
                    </span>
                    {/* Plain input, not the shared Input: its `w-full` would override `sr-only`'s
                        1px width and stretch this hidden control across the viewport (NGC-33). */}
                    <input
                      id={`df-${dataField.id}-file`}
                      type="file"
                      className="sr-only"
                      onChange={(e) => onFileSelected(dataField.id, e.target.files?.[0] ?? null)}
                    />
                  </label>
                )}
                {showSizeHint && (
                  <p className="text-muted-foreground text-xs break-words">
                    {t('instruments.taskLogs.fileAttachment.sizeHint')}
                  </p>
                )}
              </div>
            ) : fieldType === 'number' ? (
              <div className="space-y-1">
                <Input
                  id={`df-${dataField.id}`}
                  type="text"
                  inputMode="decimal"
                  value={value}
                  onChange={(e) => {
                    if (isAllowedNumericInput(e.target.value))
                      onUpdateValue(dataField.id, e.target.value)
                  }}
                  className={cn('max-w-xs', errMsg && 'border-destructive')}
                />
                {dataField.requiresValidation &&
                  dataField.acceptableValues.length > 0 &&
                  value !== '' &&
                  !Number.isNaN(parseFloat(value)) &&
                  (isValueOutOfRange(value, dataField.acceptableValues) ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
                      <Ban className="h-3.5 w-3.5" />
                      {t('instruments.taskLogs.number.unacceptable')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {t('instruments.taskLogs.number.acceptable')}
                    </span>
                  ))}
              </div>
            ) : hasDefinedOptions ? (
              <div className="space-y-1">
                <OptionButtonGroup
                  ariaLabelledBy={`df-${dataField.id}-label`}
                  value={value || ''}
                  onValueChange={(nextValue) => onUpdateValue(dataField.id, nextValue)}
                  hasError={Boolean(errMsg)}
                  options={selectOptions.map((opt) => ({
                    id: `df-${dataField.id}-${opt.value}`,
                    value: opt.value,
                    label: opt.label,
                  }))}
                />
                {entryUnacceptable && <UnacceptableBadge />}
              </div>
            ) : (
              <Input
                id={`df-${dataField.id}`}
                type="text"
                value={value}
                onChange={(e) => onUpdateValue(dataField.id, e.target.value)}
                className={cn('max-w-xs', errMsg && 'border-destructive')}
              />
            )}

            {errMsg && <p className="text-xs text-destructive">{errMsg}</p>}
            {!errMsg &&
              dataField.requiresValidation &&
              fieldType !== 'linear' &&
              fieldType !== 'number' &&
              (() => {
                if (fieldType === 'defined') {
                  if (!value || entryUnacceptable) return null
                  // Pass only: green within-range. Fail uses UnacceptableBadge above
                  // (single indicator — REBUILD-530).
                  const isAcceptablePass = dataField.acceptableValues.some(
                    (row) =>
                      row.isAcceptable &&
                      String(row.value ?? '')
                        .trim()
                        .toLowerCase() === value.trim().toLowerCase(),
                  )
                  if (!isAcceptablePass) return null
                  return (
                    <p className="flex items-center gap-1 text-xs text-green-600">
                      <Check className="h-3.5 w-3.5" />
                      {t('instruments.taskLogs.withinRange')}
                    </p>
                  )
                }
                if (isValueOutOfRange(value, dataField.acceptableValues)) {
                  return (
                    <p className="text-xs text-amber-600">
                      {t('instruments.taskLogs.validationWarning')}
                    </p>
                  )
                }
                if (dataField.acceptableValues.length > 0 && value !== '') {
                  return (
                    <p className="flex items-center gap-1 text-xs text-green-600">
                      <Check className="h-3.5 w-3.5" />
                      {t('instruments.taskLogs.withinRange')}
                    </p>
                  )
                }
                return null
              })()}
          </div>
        )
      })}
    </div>
  )

  if (!showContainer) return fields

  return <div className="space-y-4 rounded-lg border p-4">{fields}</div>
}
